import React, { useState, useEffect, useCallback } from "react";
import { MdWifiOff, MdRefresh, MdCheckCircle } from "react-icons/md";

/**
 * OfflineBoundary:
 * Self-contained network status wrapper and top accent ribbon.
 * Features:
 * - Direct native browser online/offline event detection (no external hook needed)
 * - Auto-dismissing "Back online" status banner (2.5s)
 * - Refined, subtle SaaS color palette (no dark backgrounds, no harsh yellows)
 * - True full-width alignment matching Layout Header (px-4 sm:px-6)
 * - Smooth height-animated spacer preventing layout jumps
 */
export default function OfflineBoundary({ children }) {
  const [isOnline, setIsOnline] = useState(() => {
    return typeof navigator !== "undefined" && typeof navigator.onLine === "boolean"
      ? navigator.onLine
      : true;
  });

  const [recentlyReconnected, setRecentlyReconnected] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    let timer = null;

    const handleOnline = () => {
      setIsOnline(true);
      setRecentlyReconnected(true);
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        setRecentlyReconnected(false);
      }, 2000);
    };

    const handleOffline = () => {
      if (timer) clearTimeout(timer);
      setIsOnline(false);
      setRecentlyReconnected(false);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const checkConnection = useCallback(() => {
    setIsChecking(true);
    setTimeout(() => {
      if (typeof navigator !== "undefined" && navigator.onLine) {
        setIsOnline(true);
        setRecentlyReconnected(true);
        setTimeout(() => setRecentlyReconnected(false), 2000);
      } else {
        setIsOnline(false);
      }
      setIsChecking(false);
    }, 450);
  }, []);

  const showRibbon = !isOnline || recentlyReconnected;
  const isOnlineTheme = recentlyReconnected || isOnline;

  return (
    <>
      {/* Top Accent Ribbon (Pinned at very top) */}
      <aside
        role="status"
        aria-live="polite"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-in-out transform ${
          showRibbon
            ? "translate-y-0 opacity-100 shadow-xs"
            : "-translate-y-full opacity-0 pointer-events-none"
        } ${
          isOnlineTheme
            ? "bg-emerald-50 text-emerald-900 border-b border-emerald-200/90"
            : "bg-red-50 text-red-900 border-b border-red-200/90"
        }`}
      >
        <div className="h-8 w-full px-4 sm:px-6 flex items-center justify-between text-xs select-none">
          {recentlyReconnected ? (
            /* Reconnected state (displays for 2.5s then auto-fades) */
            <div className="flex items-center gap-2 font-medium min-w-0">
              <MdCheckCircle size={15} className="text-emerald-600 shrink-0" />
              <span className="font-semibold text-emerald-950">Back online.</span>
              <span className="hidden sm:inline text-emerald-800/90 text-[11.5px] truncate">
                Your internet connection has been restored.
              </span>
            </div>
          ) : (
            /* Offline state */
            <div className="flex items-center gap-2.5 min-w-0">
              {/* Pulsing indicator dot */}
              <span className="relative flex h-2 w-2 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
              </span>

              <div className="flex items-center gap-1.5 min-w-0">
                <MdWifiOff size={14} className="text-red-600 shrink-0" />
                <div className="flex items-center gap-1 text-xs text-red-900 font-medium truncate">
                  <span className="font-semibold text-red-950">You're offline.</span>
                  <span className="hidden sm:inline text-red-800/90 text-[11.5px] truncate">
                    Working in offline mode — updates will sync once reconnected.
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Right Action side */}
          {!recentlyReconnected && (
            <div className="flex items-center shrink-0 ml-3">
              <button
                type="button"
                onClick={checkConnection}
                disabled={isChecking}
                className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-[11px] font-semibold bg-white text-red-800 border border-red-200/90 hover:bg-red-100/60 active:bg-red-100 transition-all shadow-xs cursor-pointer focus:outline-none disabled:opacity-70 disabled:cursor-not-allowed"
                title="Check internet connection"
              >
                <MdRefresh
                  size={12}
                  className={`shrink-0 ${isChecking ? "animate-spin text-red-600" : "text-red-700"}`}
                />
                <span>
                  {isChecking ? "Checking..." : "Retry now"}
                </span>
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Smooth layout spacer preventing content jump */}
      <div
        className={`w-full shrink-0 transition-all duration-300 ease-in-out overflow-hidden ${
          showRibbon ? "h-8" : "h-0"
        }`}
        aria-hidden="true"
      />

      {children}
    </>
  );
}
